import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./ArtistMode.css";
import MessageBusService from "../../services/messageBusService";
import officialLogo from "../../assets/logo/the-first-noncarbon-artist.png";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8888";
const WS_BASE_URL = API_BASE_URL.replace(/^http/, "ws");

const buildId = (prefix) =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const createFeedMessage = (role, text, extra = {}) => ({
  id: buildId(role),
  role,
  text,
  ...extra,
});

const getImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith("http")) return path;
  if (path.startsWith("/")) return `${API_BASE_URL}${path}`;
  return `${API_BASE_URL}/${path}`;
};

const buildDownloadUrl = (url, filename) => {
  const parsedUrl = new URL(url, window.location.href);
  parsedUrl.searchParams.set("download", "1");
  if (filename) {
    parsedUrl.searchParams.set("filename", filename);
  }
  return parsedUrl.toString();
};

const shouldPreferNativeShare = () => {
  if (typeof navigator === "undefined" || !navigator.share) return false;

  const userAgent = navigator.userAgent || "";
  const platform = navigator.userAgentData?.platform || navigator.platform || "";
  const maxTouchPoints = navigator.maxTouchPoints || 0;
  const isIOS =
    /iPad|iPhone|iPod/i.test(userAgent) ||
    (platform === "MacIntel" && maxTouchPoints > 1);
  const isAndroid = /Android/i.test(userAgent);

  return isIOS || isAndroid;
};

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Failed to load image"));
    image.src = src;
  });

const prepareImageData = async (file) => {
  const rawDataUrl = await fileToDataUrl(file);
  const image = await loadImage(rawDataUrl);
  const maxSide = 1400;
  let width = image.width;
  let height = image.height;

  if (Math.max(width, height) > maxSide) {
    const ratio = maxSide / Math.max(width, height);
    width = Math.round(width * ratio);
    height = Math.round(height * ratio);
  }

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");

  if (!context) {
    throw new Error("Canvas is not available");
  }

  context.drawImage(image, 0, 0, width, height);
  return canvas.toDataURL("image/jpeg", 0.86);
};

const formatSeconds = (value) => {
  const minutes = Math.floor(value / 60)
    .toString()
    .padStart(2, "0");
  const seconds = (value % 60).toString().padStart(2, "0");
  return `${minutes}:${seconds}`;
};

const buildWelcomeFeed = (nickname) => [
  createFeedMessage(
    "assistant",
    `Hello ${nickname || "there"}. This session now behaves like a small AI studio. Send a prompt, image, photo, or voice note to influence the next artwork.`
  ),
  createFeedMessage(
    "assistant",
    "Use the plus button for Gallery or Take Photo, and the mic for a voice note.",
    { subtle: true }
  ),
];

const RECEIVED_ACKNOWLEDGMENT =
  "Received. NonCarbon artist is processing your prompt now.";
const GENERATED_ARTWORK_MESSAGE =
  "New artwork returned from this session.";

const INTRO_TYPING_INTERVAL = 42;
const INTRO_TYPING_TARGET = 70;
const INTRO_MESSAGE_GAP = 620;
const TRANSCRIPT_TYPING_INTERVAL = 38;
const TRANSCRIPT_TYPING_TARGET = 56;
const SESSION_SOCKET_RECONNECT_DELAY = 1600;
const GYRO_SEND_INTERVAL = 320;
const GYRO_CHANGE_THRESHOLD = 1.5;

const hasMeaningfulGyroChange = (current, previous) => {
  if (!previous) return true;

  const keys = ["alpha", "beta", "gamma"];
  for (const key of keys) {
    const nextValue = current?.[key];
    const previousValue = previous?.[key];

    if (nextValue == null && previousValue == null) {
      continue;
    }

    if (nextValue == null || previousValue == null) {
      return true;
    }

    if (Math.abs(nextValue - previousValue) >= GYRO_CHANGE_THRESHOLD) {
      return true;
    }
  }

  return current?.absolute !== previous?.absolute;
};

const PlusIcon = (props) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    {...props}
  >
    <path
      d="M12 5V19"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M5 12H19"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SendIcon = (props) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    {...props}
  >
    <path
      d="M12 5.25L12 18.75"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M18.75 12L12 5.25L5.25 12"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const MicIcon = (props) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    {...props}
  >
    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
    <line x1="12" y1="19" x2="12" y2="23" />
  </svg>
);

const CameraIcon = (props) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    {...props}
  >
    <path d="M4 7h3l1.6-2h6.8L17 7h3a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2z" />
    <circle cx="12" cy="13" r="3.5" />
  </svg>
);

const GalleryIcon = (props) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    {...props}
  >
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <circle cx="9" cy="10" r="1.5" />
    <path d="M21 16l-5.5-5.5L7 19" />
  </svg>
);

const GyroIcon = (props) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    {...props}
  >
    <circle cx="12" cy="12" r="2.2" />
    <ellipse cx="12" cy="12" rx="8" ry="4.8" />
    <path d="M12 4C16.4 4 20 7.58 20 12C20 16.42 16.4 20 12 20" />
    <path d="M12 4C7.6 4 4 7.58 4 12C4 16.42 7.6 20 12 20" />
  </svg>
);

const ArtistMode = ({ sessionId, nickname }) => {
  const [prompt, setPrompt] = useState("");
  const [draftImage, setDraftImage] = useState(null);
  const [draftLabel, setDraftLabel] = useState("");
  const [feed, setFeed] = useState([]);
  const [notice, setNotice] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [isComposerOpen, setIsComposerOpen] = useState(false);
  const [isAttachmentMenuOpen, setIsAttachmentMenuOpen] = useState(false);
  const [audioWidgetPhase, setAudioWidgetPhase] = useState("idle");
  const [audioWidgetText, setAudioWidgetText] = useState("");
  const [audioWidgetDisplayText, setAudioWidgetDisplayText] = useState("");
  const [isGyroEnabled, setIsGyroEnabled] = useState(false);

  const galleryInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const audioInputRef = useRef(null);
  const attachmentMenuRef = useRef(null);
  const recorderRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const audioChunksRef = useRef([]);
  const noticeTimeoutRef = useRef(null);
  const refreshTimeoutsRef = useRef([]);
  const audioWidgetTimeoutRef = useRef(null);
  const audioWidgetIntervalRef = useRef(null);
  const introTimeoutsRef = useRef([]);
  const composerTextareaRef = useRef(null);
  const feedEndRef = useRef(null);
  const deliveredArtworkIdsRef = useRef(new Set());
  const deliveredSessionEventsRef = useRef(new Set());
  const introSequenceEndsRef = useRef(0);
  const sessionSocketRef = useRef(null);
  const sessionSocketReconnectRef = useRef(null);
  const sessionSocketReadyRef = useRef(false);
  const gyroDataRef = useRef({
    alpha: null,
    beta: null,
    gamma: null,
    absolute: null,
  });
  const lastSentGyroRef = useRef(null);
  const gyroTransportBlockedRef = useRef(false);

  const flashNotice = useCallback((type, text) => {
    if (noticeTimeoutRef.current) {
      clearTimeout(noticeTimeoutRef.current);
    }
    setNotice({ type, text });
    noticeTimeoutRef.current = setTimeout(() => {
      setNotice(null);
      noticeTimeoutRef.current = null;
    }, 2600);
  }, []);

  const appendFeed = useCallback((message) => {
    setFeed((current) => [...current, message]);
  }, []);

  const appendReceivedAcknowledgment = useCallback(() => {
    appendFeed(createFeedMessage("assistant", RECEIVED_ACKNOWLEDGMENT));
  }, [appendFeed]);

  const clearIntroTimers = useCallback(() => {
    introTimeoutsRef.current.forEach((timeoutId) => clearTimeout(timeoutId));
    introTimeoutsRef.current = [];
  }, []);

  const scheduleIntroTimeout = useCallback((callback, delay) => {
    const timeoutId = setTimeout(callback, delay);
    introTimeoutsRef.current.push(timeoutId);
    return timeoutId;
  }, []);

  const replayWelcomeFeed = useCallback(
    (nextNickname) => {
      clearIntroTimers();
      const welcomeMessages = buildWelcomeFeed(nextNickname);
      setFeed([]);

      let delay = 180;

      welcomeMessages.forEach((message) => {
        const fullText = message.text;
        const step = Math.max(1, Math.ceil(fullText.length / INTRO_TYPING_TARGET));
        const steps = Math.ceil(fullText.length / step);

        scheduleIntroTimeout(() => {
          setFeed((current) => [...current, { ...message, text: "", typing: true }]);

          let cursor = 0;
          const revealChunk = () => {
            cursor = Math.min(fullText.length, cursor + step);

            setFeed((current) =>
              current.map((item) =>
                item.id === message.id
                  ? {
                      ...item,
                      text: fullText.slice(0, cursor),
                      typing: cursor < fullText.length,
                    }
                  : item
              )
            );

            if (cursor < fullText.length) {
              scheduleIntroTimeout(revealChunk, INTRO_TYPING_INTERVAL);
            }
          };

          revealChunk();
        }, delay);

        delay += steps * INTRO_TYPING_INTERVAL + INTRO_MESSAGE_GAP;
      });

      introSequenceEndsRef.current = Date.now() + Math.max(0, delay - INTRO_MESSAGE_GAP);
    },
    [clearIntroTimers, scheduleIntroTimeout]
  );

  const clearAudioWidgetTimers = useCallback(() => {
    if (audioWidgetTimeoutRef.current) {
      clearTimeout(audioWidgetTimeoutRef.current);
      audioWidgetTimeoutRef.current = null;
    }
    if (audioWidgetIntervalRef.current) {
      clearInterval(audioWidgetIntervalRef.current);
      audioWidgetIntervalRef.current = null;
    }
  }, []);

  const resetAudioWidget = useCallback(() => {
    clearAudioWidgetTimers();
    setAudioWidgetPhase("idle");
    setAudioWidgetText("");
    setAudioWidgetDisplayText("");
  }, [clearAudioWidgetTimers]);

  const stageAudioTranscript = useCallback(
    (transcript, acknowledgmentText = null, inputId = null) => {
      const finalText = transcript?.trim();

      if (!finalText) {
        clearAudioWidgetTimers();
        setAudioWidgetPhase("reveal");
        setAudioWidgetText("Voice note processed.");
        setAudioWidgetDisplayText("Voice note processed.");
        audioWidgetTimeoutRef.current = setTimeout(() => {
          resetAudioWidget();
        }, 1100);
        return;
      }

      clearAudioWidgetTimers();
      setAudioWidgetPhase("reveal");
      setAudioWidgetText(finalText);
      setAudioWidgetDisplayText("");

      let index = 0;
      const step = Math.max(1, Math.ceil(finalText.length / TRANSCRIPT_TYPING_TARGET));

      audioWidgetIntervalRef.current = setInterval(() => {
        index = Math.min(finalText.length, index + step);
        setAudioWidgetDisplayText(finalText.slice(0, index));

        if (index >= finalText.length) {
          clearAudioWidgetTimers();
          audioWidgetTimeoutRef.current = setTimeout(() => {
            appendFeed(
              createFeedMessage("user", finalText, {
                inputType: "voice",
                inputId,
              })
            );
            if (acknowledgmentText) {
              appendFeed(createFeedMessage("assistant", acknowledgmentText));
            }
            resetAudioWidget();
          }, 1300);
        }
      }, TRANSCRIPT_TYPING_INTERVAL);
    },
    [appendFeed, clearAudioWidgetTimers, resetAudioWidget]
  );

  const clearDraftImage = useCallback(() => {
    setDraftImage(null);
    setDraftLabel("");
  }, []);

  const handleDownloadImage = useCallback(
    async (url, filename = "noncarbon-artwork.jpg") => {
      const downloadUrl = buildDownloadUrl(url, filename);

      try {
        if (shouldPreferNativeShare()) {
          try {
            await navigator.share({
              url,
              title: "NonCarbon artwork",
              text: "Generated artwork from TFNCA",
            });
            return;
          } catch (shareError) {
            if (shareError?.name === "AbortError") {
              return;
            }
          }
        }

        const link = document.createElement("a");
        link.href = downloadUrl;
        link.rel = "noopener,noreferrer";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        window.open(downloadUrl, "_blank", "noopener,noreferrer");
        flashNotice("success", "Opened artwork download.");
      }
    },
    [flashNotice]
  );

  const appendGeneratedArtworkMessage = useCallback(
    ({ image_id: imageId, image_url: imageUrl, message }) => {
      const artworkKey = imageId ?? imageUrl;
      if (!artworkKey || deliveredArtworkIdsRef.current.has(artworkKey)) return;

      deliveredArtworkIdsRef.current.add(artworkKey);

      const nextMessage = createFeedMessage(
        "assistant",
        message || GENERATED_ARTWORK_MESSAGE,
        {
          image: getImageUrl(imageUrl),
          downloadUrl: getImageUrl(imageUrl),
          downloadName: `noncarbon-artwork-${artworkKey}.jpg`,
          artworkId: artworkKey,
        }
      );

      const waitForIntro = Math.max(0, introSequenceEndsRef.current - Date.now());

      if (waitForIntro > 0) {
        const timeoutId = setTimeout(() => {
          setFeed((current) => [...current, nextMessage]);
        }, waitForIntro + 120);
        refreshTimeoutsRef.current.push(timeoutId);
        return;
      }

      setFeed((current) => [...current, nextMessage]);
    },
    []
  );

  const handleSessionEvent = useCallback(
    (event) => {
      const payload = event?.payload || {};
      const eventKey =
        payload.input_id
          ? `${event.type}:${payload.input_id}:${payload.timestamp || ""}`
          : payload.image_id
            ? `${event.type}:${payload.image_id}`
            : `${event.type}:${payload.timestamp || ""}:${payload.image_url || payload.message || ""}`;

      if (deliveredSessionEventsRef.current.has(eventKey)) return;
      deliveredSessionEventsRef.current.add(eventKey);

      if (event.type === "input_acknowledged") {
        const acknowledgementText = payload.message || RECEIVED_ACKNOWLEDGMENT;
        if (payload.kind === "audio" && payload.input_id) {
          stageAudioTranscript(
            payload.text,
            acknowledgementText,
            payload.input_id || null
          );
          return;
        }

        appendFeed(createFeedMessage("assistant", acknowledgementText));
        return;
      }

      if (event.type === "image_generated") {
        appendGeneratedArtworkMessage(payload);
        return;
      }

      if (event.type === "input_failed") {
        appendFeed(
          createFeedMessage(
            "assistant",
            payload.message || `Send failed: ${payload.error_message || "unknown error"}`,
            { error: true }
          )
        );
      }
    },
    [appendFeed, appendGeneratedArtworkMessage, stageAudioTranscript]
  );

  const stopMediaStream = useCallback(() => {
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }
  }, []);

  const uploadAudio = useCallback(
    async (audioBlob, sourceLabel = "Voice note") => {
      clearAudioWidgetTimers();
      setAudioWidgetPhase("processing");
      setAudioWidgetText("");
      setAudioWidgetDisplayText("");
      setIsSubmitting(true);

      try {
        const result = await MessageBusService.sendAudioForTranscription(
          audioBlob,
          sessionId,
          nickname
        );
        const transcript = result?.transcript?.trim();

        if (result?.status !== "success" || !transcript) {
          throw new Error(
            result?.transcription_error ||
              "Audio transcription failed."
          );
        }

        flashNotice("success", "Voice note received.");
        if (!sessionSocketReadyRef.current) {
          stageAudioTranscript(transcript, RECEIVED_ACKNOWLEDGMENT);
        }
      } catch (error) {
        clearAudioWidgetTimers();
        setAudioWidgetPhase("error");
        setAudioWidgetText("Voice note failed.");
        setAudioWidgetDisplayText("Voice note failed.");
        audioWidgetTimeoutRef.current = setTimeout(() => {
          resetAudioWidget();
        }, 1500);
        appendFeed(
          createFeedMessage(
            "assistant",
            `Voice note failed: ${error.message || "unknown error"}`,
            { error: true }
          )
        );
        flashNotice("error", "Voice note failed to send.");
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      appendFeed,
      clearAudioWidgetTimers,
      flashNotice,
      nickname,
      resetAudioWidget,
      sessionId,
      stageAudioTranscript,
    ]
  );

  const startRecording = useCallback(async () => {
    if (
      !navigator.mediaDevices?.getUserMedia ||
      typeof MediaRecorder === "undefined"
    ) {
      audioInputRef.current?.click();
      return;
    }

    try {
      clearAudioWidgetTimers();
      setIsAttachmentMenuOpen(false);
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const preferredMime =
        MediaRecorder.isTypeSupported &&
        MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
          ? "audio/webm;codecs=opus"
          : MediaRecorder.isTypeSupported &&
              MediaRecorder.isTypeSupported("audio/webm")
            ? "audio/webm"
            : "";

      mediaStreamRef.current = stream;
      audioChunksRef.current = [];

      const recorder = preferredMime
        ? new MediaRecorder(stream, { mimeType: preferredMime })
        : new MediaRecorder(stream);

      recorderRef.current = recorder;
      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      recorder.onstop = async () => {
        const blob = new Blob(audioChunksRef.current, {
          type: recorder.mimeType || "audio/webm",
        });
        audioChunksRef.current = [];
        recorderRef.current = null;
        stopMediaStream();
        if (blob.size > 0) {
          await uploadAudio(blob);
        }
      };

      recorder.start();
      setIsRecording(true);
      setAudioWidgetPhase("recording");
      setAudioWidgetText("");
      setAudioWidgetDisplayText("");
      flashNotice("success", "Recording started. Tap Audio again to send.");
    } catch (error) {
      flashNotice("error", "Microphone access was blocked.");
    }
  }, [clearAudioWidgetTimers, flashNotice, stopMediaStream, uploadAudio]);

  const stopRecording = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
      setAudioWidgetPhase("processing");
      setAudioWidgetText("");
      setAudioWidgetDisplayText("");
    } else {
      stopMediaStream();
      resetAudioWidget();
    }
    setIsRecording(false);
  }, [resetAudioWidget, stopMediaStream]);

  const handleAudioAction = useCallback(() => {
    if (isSubmitting) return;
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  }, [isRecording, isSubmitting, startRecording, stopRecording]);

  const handleAudioFileChange = useCallback(
    async (event) => {
      const file = event.target.files?.[0];
      event.target.value = "";
      if (!file) return;
      setIsAttachmentMenuOpen(false);
      await uploadAudio(file, `Audio file: ${file.name}`);
    },
    [uploadAudio]
  );

  const handleGyroToggle = useCallback(async () => {
    if (isGyroEnabled) {
      setIsGyroEnabled(false);
      flashNotice("success", "Gyroscope controls turned off.");
      return;
    }

    try {
      const OrientationEventCtor =
        typeof window !== "undefined" ? window.DeviceOrientationEvent : undefined;

      if (
        OrientationEventCtor &&
        typeof OrientationEventCtor.requestPermission === "function"
      ) {
        const permission = await OrientationEventCtor.requestPermission();
        if (permission !== "granted") {
          flashNotice("error", "Gyroscope access was not granted.");
          return;
        }
      }

      setIsGyroEnabled(true);
      flashNotice("success", "Gyroscope controls turned on.");
    } catch (error) {
      flashNotice("error", "Gyroscope access was blocked.");
    }
  }, [flashNotice, isGyroEnabled]);

  const handleImageSelection = useCallback(
    async (file, sourceLabel) => {
      if (!file) return;

      try {
        const dataUrl = await prepareImageData(file);
        setDraftImage(dataUrl);
        setDraftLabel(`${sourceLabel}: ${file.name || "image"}`);
        setIsAttachmentMenuOpen(false);
        flashNotice("success", `${sourceLabel} is ready to send.`);
      } catch (error) {
        flashNotice("error", `Couldn't prepare the ${sourceLabel.toLowerCase()}.`);
      }
    },
    [flashNotice]
  );

  const handleSubmit = useCallback(async () => {
    const trimmedPrompt = prompt.trim();
    if ((!trimmedPrompt && !draftImage) || isSubmitting) return;

    appendFeed(
      createFeedMessage(
        "user",
        trimmedPrompt || draftLabel || "Image attached.",
        draftImage ? { image: draftImage } : {}
      )
    );

    setIsSubmitting(true);
    try {
      await MessageBusService.sendSensorFrameWithTranscriptToMessageBus(
        draftImage,
        trimmedPrompt,
        sessionId,
        nickname
      );
      flashNotice("success", "Message sent.");

      setPrompt("");
      clearDraftImage();
      if (!sessionSocketReadyRef.current) {
        appendReceivedAcknowledgment();
      }
    } catch (error) {
      appendFeed(
        createFeedMessage(
          "assistant",
          `Send failed: ${error.message || "unknown error"}`,
          { error: true }
        )
      );
      flashNotice("error", "Message failed to send.");
    } finally {
      setIsSubmitting(false);
    }
  }, [
    appendFeed,
    appendReceivedAcknowledgment,
    clearDraftImage,
    draftImage,
    draftLabel,
    flashNotice,
    isSubmitting,
    nickname,
    prompt,
    sessionId,
  ]);

  useEffect(() => {
    replayWelcomeFeed(nickname);
    deliveredArtworkIdsRef.current.clear();
    deliveredSessionEventsRef.current.clear();
    introSequenceEndsRef.current = 0;
    setPrompt("");
    clearDraftImage();
    resetAudioWidget();
    setIsComposerOpen(false);
    setIsAttachmentMenuOpen(false);
  }, [clearDraftImage, nickname, replayWelcomeFeed, resetAudioWidget, sessionId]);

  useEffect(() => {
    if (!isRecording) {
      setRecordingSeconds(0);
      return undefined;
    }

    const startedAt = Date.now();
    const intervalId = setInterval(() => {
      setRecordingSeconds(Math.floor((Date.now() - startedAt) / 1000));
    }, 300);
    return () => clearInterval(intervalId);
  }, [isRecording]);

  useEffect(() => {
    feedEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [feed]);

  useEffect(() => {
    if (!sessionId) return undefined;

    let cancelled = false;

    const connect = () => {
      if (cancelled) return;

      const socket = new WebSocket(
        `${WS_BASE_URL}/ws/session/${encodeURIComponent(sessionId)}`
      );
      sessionSocketRef.current = socket;

      socket.onopen = () => {
        sessionSocketReadyRef.current = true;
      };

      socket.onmessage = (event) => {
        try {
          handleSessionEvent(JSON.parse(event.data));
        } catch (error) {
          console.error("Session socket message error:", error);
        }
      };

      socket.onclose = () => {
        if (sessionSocketRef.current === socket) {
          sessionSocketRef.current = null;
        }
        sessionSocketReadyRef.current = false;

        if (!cancelled) {
          sessionSocketReconnectRef.current = setTimeout(
            connect,
            SESSION_SOCKET_RECONNECT_DELAY
          );
        }
      };

      socket.onerror = () => {
        socket.close();
      };
    };

    connect();

    return () => {
      cancelled = true;
      sessionSocketReadyRef.current = false;
      if (sessionSocketReconnectRef.current) {
        clearTimeout(sessionSocketReconnectRef.current);
        sessionSocketReconnectRef.current = null;
      }

      if (
        sessionSocketRef.current &&
        (sessionSocketRef.current.readyState === WebSocket.OPEN ||
          sessionSocketRef.current.readyState === WebSocket.CONNECTING)
      ) {
        sessionSocketRef.current.close();
      }
      sessionSocketRef.current = null;
    };
  }, [handleSessionEvent, sessionId]);

  useEffect(() => {
    if (!isGyroEnabled) return undefined;

    const handleOrientation = (event) => {
      gyroDataRef.current = {
        alpha: event.alpha ?? null,
        beta: event.beta ?? null,
        gamma: event.gamma ?? null,
        absolute: event.absolute ?? null,
      };
    };

    window.addEventListener("deviceorientation", handleOrientation);
    return () => {
      window.removeEventListener("deviceorientation", handleOrientation);
    };
  }, [isGyroEnabled]);

  useEffect(() => {
    if (!isGyroEnabled || !sessionId) {
      gyroTransportBlockedRef.current = false;
      lastSentGyroRef.current = null;
      return undefined;
    }

    let cancelled = false;

    const intervalId = setInterval(async () => {
      if (cancelled || gyroTransportBlockedRef.current || isSubmitting) {
        return;
      }

      const nextPayload = gyroDataRef.current;
      const hasAnyValue =
        nextPayload.alpha != null ||
        nextPayload.beta != null ||
        nextPayload.gamma != null;

      if (!hasAnyValue) {
        return;
      }

      if (!hasMeaningfulGyroChange(nextPayload, lastSentGyroRef.current)) {
        return;
      }

      try {
        await MessageBusService.sendGyroscopeDataToMessageBus(
          nextPayload,
          sessionId,
          nickname
        );
        lastSentGyroRef.current = { ...nextPayload };
      } catch (error) {
        gyroTransportBlockedRef.current = true;
        console.error("Gyroscope publish failed:", error);
        flashNotice("error", "Gyroscope streaming failed.");
      }
    }, GYRO_SEND_INTERVAL);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
      gyroTransportBlockedRef.current = false;
      lastSentGyroRef.current = null;
    };
  }, [flashNotice, isGyroEnabled, isSubmitting, nickname, sessionId]);

  useEffect(() => {
    const refreshTimeouts = refreshTimeoutsRef.current;

    return () => {
      stopMediaStream();
      if (noticeTimeoutRef.current) clearTimeout(noticeTimeoutRef.current);
      refreshTimeouts.forEach((timeoutId) => clearTimeout(timeoutId));
      if (sessionSocketReconnectRef.current) {
        clearTimeout(sessionSocketReconnectRef.current);
      }
      if (
        sessionSocketRef.current &&
        (sessionSocketRef.current.readyState === WebSocket.OPEN ||
          sessionSocketRef.current.readyState === WebSocket.CONNECTING)
      ) {
        sessionSocketRef.current.close();
      }
      clearAudioWidgetTimers();
      clearIntroTimers();
    };
  }, [clearAudioWidgetTimers, clearIntroTimers, stopMediaStream]);

  useEffect(() => {
    if (!isAttachmentMenuOpen) return undefined;

    const handlePointerDown = (event) => {
      if (
        attachmentMenuRef.current &&
        !attachmentMenuRef.current.contains(event.target)
      ) {
        setIsAttachmentMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
    };
  }, [isAttachmentMenuOpen]);

  const canSend = prompt.trim().length > 0 || Boolean(draftImage);
  const isComposerLockedOpen =
    canSend || isRecording || isSubmitting || audioWidgetPhase !== "idle";

  useEffect(() => {
    if (isComposerLockedOpen) {
      setIsComposerOpen(true);
    }
  }, [isComposerLockedOpen]);

  useEffect(() => {
    if (!isComposerOpen) return undefined;

    const frameId = window.requestAnimationFrame(() => {
      composerTextareaRef.current?.focus();
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [isComposerOpen]);

  const audioWidgetLabel = useMemo(() => {
    if (audioWidgetPhase === "recording") return "Listening";
    if (audioWidgetPhase === "processing") return "Processing Voice";
    if (audioWidgetPhase === "error") return "Audio Error";
    if (audioWidgetPhase === "reveal") return "Transcript Ready";
    return "";
  }, [audioWidgetPhase]);
  const audioWidgetCopy = useMemo(() => {
    if (audioWidgetPhase === "recording") {
      return `Capturing voice note ${formatSeconds(recordingSeconds)}`;
    }
    if (audioWidgetPhase === "processing") {
      return "Sending audio to the backend and waiting for transcription...";
    }
    if (audioWidgetPhase === "error") {
      return audioWidgetDisplayText || audioWidgetText || "Voice note failed.";
    }
    if (audioWidgetPhase === "reveal") {
      return audioWidgetDisplayText || audioWidgetText;
    }
    return "";
  }, [
    audioWidgetDisplayText,
    audioWidgetPhase,
    audioWidgetText,
    recordingSeconds,
  ]);

  return (
    <div className="artist-mode">
      {notice && (
        <div className={`qr-notice ${notice.type === "error" ? "error" : "success"}`}>
          {notice.text}
        </div>
      )}

      <header className="assistant-header">
        <div className="assistant-brand-mark" aria-hidden="true">
          <img
            src={officialLogo}
            alt="The First NonCarbon Artist"
            className="assistant-brand-image"
          />
        </div>
        <div className="assistant-copy">
          <h1>{nickname ? `Hello ${nickname}` : "Hello"}</h1>
          <p>
            Guide the next artwork with a short prompt, an image, a fresh photo,
            or a voice note.
          </p>
        </div>
        {/* Refresh stays intentionally hidden while the session feed is websocket-driven. */}
      </header>

      {/* Keeping the current-canvas preview block in place for later iteration.
      <section className="preview-card">
        <div className="preview-meta">
          <div>
            <span className="preview-label">Current canvas</span>
            <strong>
              {draftImage
                ? "Attached image ready to send"
                : selectedArtwork
                  ? "Latest artwork from this session"
                  : "Nothing generated yet"}
            </strong>
          </div>
          <span className="preview-chip">Session {sessionLabel}</span>
        </div>

        <div className="preview-frame">
          {previewImage ? (
            <img src={previewImage} alt="Session preview" className="preview-image" />
          ) : (
            <div className="preview-empty">
              Send something to start this conversation.
            </div>
          )}
        </div>

        {draftImage ? (
          <div className="draft-chip">
            <span>{draftLabel || "Image attached"}</span>
            <button type="button" onClick={clearDraftImage}>
              Remove
            </button>
          </div>
        ) : (
          <p className="preview-note">
            {selectedArtwork
              ? "Tap a thumbnail below to switch between your recent outputs."
              : "Nothing has come back from this QR session yet."}
          </p>
        )}
      </section>
      */}

      <section className="conversation-panel">
        <div className="conversation-feed">
          {feed.map((message) => (
            <div
              key={message.id}
              className={`message-row ${message.role === "user" ? "user" : "assistant"}`}
            >
              <div
                className={`message-bubble ${message.role} ${message.error ? "error" : ""}`}
              >
                <span className="bubble-label">
                  {message.role === "user"
                    ? `${nickname || "You"}${
                        message.inputType === "voice"
                          ? " • Voice"
                          : message.image
                            ? " • Image"
                            : ""
                      }`
                    : "NonCarbon artist"}
                </span>
                <p className={`bubble-text ${message.typing ? "typing" : ""}`}>
                  {message.text}
                </p>
                {message.image && (
                  <div className="bubble-image-wrap">
                    <img
                      src={message.image}
                      alt={message.downloadUrl ? "Generated artwork" : "Sent attachment"}
                      className="bubble-image"
                    />
                    {message.downloadUrl && (
                      <button
                        type="button"
                        className="bubble-download"
                        onClick={() =>
                          handleDownloadImage(message.downloadUrl, message.downloadName)
                        }
                        aria-label="Download artwork"
                      >
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          aria-hidden="true"
                        >
                          <path
                            d="M12 4V14"
                            stroke="currentColor"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M8 10L12 14L16 10"
                            stroke="currentColor"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M5 19H19"
                            stroke="currentColor"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
          <div ref={feedEndRef} />
        </div>
      </section>

      <section className={`composer-panel ${isComposerOpen ? "open" : "collapsed"}`}>
        {!isComposerOpen ? (
          <button
            type="button"
            className="composer-launch-button"
            onClick={() => setIsComposerOpen(true)}
            disabled={isSubmitting}
          >
            <span className="composer-launch-orb" aria-hidden="true" />
            <span className="composer-launch-copy">
              <span className="composer-launch-title">Talk with TFNCA</span>
              <span className="composer-launch-subtitle">
                Prompt, gallery, photo, or voice note
              </span>
            </span>
          </button>
        ) : (
          <>
            <div className="composer-toolbar">
              <button
                type="button"
                className="composer-toolbar-pill"
                onClick={() => composerTextareaRef.current?.focus()}
              >
                <span className="composer-toolbar-orb" aria-hidden="true" />
                <span>Talk with TFNCA</span>
              </button>

              {!isComposerLockedOpen && (
                <button
                  type="button"
                  className="composer-collapse-button"
                  onClick={() => {
                    setIsAttachmentMenuOpen(false);
                    setIsComposerOpen(false);
                  }}
                >
                  Close
                </button>
              )}
            </div>

            {audioWidgetPhase !== "idle" && (
              <div className={`audio-widget ${audioWidgetPhase}`}>
                <div className={`audio-widget__orb ${audioWidgetPhase}`} aria-hidden="true" />
                <div className="audio-widget__copy">
                  <span className="audio-widget__eyebrow">{audioWidgetLabel}</span>
                  <p
                    className={`audio-widget__text ${
                      audioWidgetPhase === "reveal" && audioWidgetDisplayText !== audioWidgetText
                        ? "typing"
                        : ""
                    }`}
                  >
                    {audioWidgetCopy}
                  </p>
                  {audioWidgetPhase === "processing" && (
                    <div className="audio-widget__dots" aria-hidden="true">
                      <span />
                      <span />
                      <span />
                    </div>
                  )}
                </div>
              </div>
            )}

            {draftImage && (
              <div className="composer-status attachment">
                <span>{draftLabel || "Image attached"}</span>
                <button type="button" onClick={clearDraftImage} aria-label="Remove attached image">
                  <span aria-hidden="true">×</span>
                </button>
              </div>
            )}

            <textarea
              ref={composerTextareaRef}
              className="composer-textarea"
              placeholder="Describe the next image, mood, texture, subject, or idea..."
              value={prompt}
              onChange={(event) => setPrompt(event.target.value)}
              maxLength={300}
              disabled={isSubmitting}
            />

            <div className="composer-actions">
              <div className="action-cluster">
                <div className="attachment-menu" ref={attachmentMenuRef}>
                  <button
                    type="button"
                    className="icon-button action-button"
                    onClick={() => setIsAttachmentMenuOpen((open) => !open)}
                    disabled={isSubmitting || isRecording}
                    aria-label="Open attachment options"
                    aria-expanded={isAttachmentMenuOpen}
                  >
                    <PlusIcon />
                  </button>

                  {isAttachmentMenuOpen && (
                    <div className="attachment-popover">
                      <button
                        type="button"
                        className="attachment-popover__item"
                        onClick={() => {
                          setIsAttachmentMenuOpen(false);
                          cameraInputRef.current?.click();
                        }}
                      >
                        <CameraIcon />
                        <span>Take Photo</span>
                      </button>
                      <button
                        type="button"
                        className="attachment-popover__item"
                        onClick={() => {
                          setIsAttachmentMenuOpen(false);
                          galleryInputRef.current?.click();
                        }}
                      >
                        <GalleryIcon />
                        <span>Gallery</span>
                      </button>
                    </div>
                  )}
                </div>

                <button
                  type="button"
                  className={`icon-button action-button ${isGyroEnabled ? "toggled" : ""}`}
                  onClick={handleGyroToggle}
                  disabled={isSubmitting}
                  aria-label={isGyroEnabled ? "Disable gyroscope" : "Enable gyroscope"}
                  aria-pressed={isGyroEnabled}
                  title={isGyroEnabled ? "Gyroscope on" : "Gyroscope off"}
                >
                  <GyroIcon />
                </button>
              </div>

              <div className="action-cluster action-cluster--right">
                <button
                  type="button"
                  className={`icon-button action-button ${isRecording ? "recording" : ""}`}
                  onClick={handleAudioAction}
                  disabled={isSubmitting}
                  aria-label={isRecording ? "Stop audio recording" : "Record voice"}
                >
                  <MicIcon />
                </button>

                <button
                  type="button"
                  className="icon-button send-button"
                  onClick={handleSubmit}
                  disabled={!canSend || isSubmitting || isRecording}
                  aria-label="Send message"
                >
                  <SendIcon />
                </button>
              </div>
            </div>
          </>
        )}

        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = "";
            handleImageSelection(file, "Camera capture");
          }}
          style={{ display: "none" }}
        />

        <input
          ref={galleryInputRef}
          type="file"
          accept="image/*"
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = "";
            handleImageSelection(file, "Gallery image");
          }}
          style={{ display: "none" }}
        />

        <input
          ref={audioInputRef}
          type="file"
          accept="audio/*"
          capture
          onChange={handleAudioFileChange}
          style={{ display: "none" }}
        />
      </section>
    </div>
  );
};

export default ArtistMode;
